import React, { useEffect, useRef, useState } from 'react';
import { Block, ImageBlockContent, LinkBlockContent, PdfBlockContent } from '../types';
import { ControlPanelSwitchButton } from './control-panel/switch';
import { useBlockActions } from '../helpers/actions/use-block-actions.hook';
import { apiGetAllContents, apiToggleContentLibrary } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Content } from 'src/common/interfaces/content.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';

interface Props {
  block: Block;
  contentObject: { [key: string]: any };
  icon?: JSX.Element | undefined;
}

function usePreviousValue(blockContent: LinkBlockContent | ImageBlockContent) {
  const ref = useRef<LinkBlockContent | ImageBlockContent | undefined>();
  useEffect(() => {
    ref.current = blockContent;
  });
  return ref.current;
}


export const ToggleVideoDocPersonalContent = ({ block, contentObject, icon }: Props) => {
  const blockContent = block?.content as LinkBlockContent | ImageBlockContent;
  const previousBlockContent = usePreviousValue(blockContent);

  const [contentsList, setContentsLists] = useState<Content[] | undefined>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingToggle, setLoadingToggle] = useState(false);

  const { onBlockParamsChange } = useBlockActions(block);
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const fetchContents = async (page = 1) => {
    const response = await apiGetAllContents(useCurrentOrganization.getState().currentOrganization.id, { page });
    setContentsLists(response?.contents);
    setTotalPages(response?.pages);
    setCurrentPage(page);
    return {
      contents: response?.contents,
      pages: response?.pages,
    };
  };


  useEffect(()=>{
    let isDeleted=true;
    if(block?.content){
      const localBlockContent = block?.content as LinkBlockContent | ImageBlockContent;
      if(localBlockContent?.contentUUID && previousBlockContent && 'fileUploadStatus' in previousBlockContent && previousBlockContent.fileUploadStatus==='in-progress'){
        handleToggleContentLibrary(false,true)
      }
    }

  },[block?.content])

  const checkContentUUID = async() =>{

    if(block?.id){

      setLoadingToggle(true);

      const blockContentUUID = blockContent?.contentUUID;
      if(blockContentUUID){

        const contentsList = await fetchContents()

        let contentFound = false;
        let currentPageIndex = currentPage;
        let content = contentsList?.contents.find((content: any) => `${content.uuid}` == `${blockContentUUID}`);
  
        while (!content && currentPageIndex < totalPages && !contentFound) {
          const { contents: newContentsList } = await fetchContents(currentPageIndex + 1);
          content = newContentsList.find((content: any) => `${content.uuid}` == `${blockContent.contentUUID}`);
          currentPageIndex++;
          contentFound = !!content;
        }

        if(!content || content.should_show_library===false){
          onBlockParamsChange(block.id, {
            personalContent: true,
            contentObject,
            staticShow: true
          });

          setLoadingToggle(false);

          return;
        }
      
      }

      onBlockParamsChange(block.id, {
        personalContent: false,
        contentObject,
        staticShow: true
      });

      setLoadingToggle(false);
   
    }
  }

  useEffect(()=>{
      checkContentUUID()
  },[])


  useEffect(() => {
    fetchContents();
  }, [block]);

 

  const handleToggleContentLibrary = async (value: boolean, suppressNotification: boolean=false) => {
    const blockContentUUID = blockContent?.contentUUID;
    if (blockContentUUID) {
      try {
        await apiToggleContentLibrary(blockContentUUID, value);
        onBlockParamsChange(block.id, {
          personalContent: !value,
          contentObject,
        });
        if(!suppressNotification){
          setSuccessNotification(!value ? 'Content removed from the library' : 'Content added to the library');
        }
      } catch (error) {
        if(!suppressNotification){
          setErrorNotification('It was not possible to update the content');
        }
      }
    }
  };

  return (
    <ControlPanelSwitchButton
      label='Add to Library'
      disabled={!blockContent.contentUUID || loadingToggle}
      icon={icon}
      checked={!blockContent.personalContent}
      onChange={(value) => handleToggleContentLibrary(value)}
    />
  );
};